import dayjs from 'dayjs';
import {
  CATEGORY_OPTIONS,
  WEEKDAYS,
} from '../components/protocols/ProtocolDaysEditor';
import { DURATION_OPTIONS, WEIGHT_RANGE_OPTIONS, labelsFor } from './protocol-form.util';

const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const labelFor = (options, value, fallback = '-') =>
  options.find((option) => option.value === value)?.label || fallback;

// The review step shows the Serbian copy only, so the exported document
// mirrors that — it is meant for the client/patient, not for translators.
const activityRow = (activity) => {
  const categoryLabel = labelFor(
    CATEGORY_OPTIONS,
    activity.category,
    activity.category
  );
  const images = (activity.imageUrls || [])
    .map((image) => `<img class="thumb" src="${escapeHtml(image)}" alt="" />`)
    .join('');

  return `
      <tr>
        <td class="time">${escapeHtml(activity.time || '-')}</td>
        <td>${escapeHtml(categoryLabel)}</td>
        <td class="name">${images}<span>${escapeHtml(activity.name?.sr || '-')}</span></td>
        <td>${escapeHtml(activity.dosage || '-')}</td>
        <td>${escapeHtml(activity.content?.sr || '-')}</td>
      </tr>`;
};

// The editor labels get away with a flat "N aktivnosti", but this document is
// read by the client, so 1 and 21 take the singular.
const activityCountLabel = (count) => {
  const singular = count % 10 === 1 && count % 100 !== 11;
  return `${count} ${singular ? 'aktivnost' : 'aktivnosti'}`;
};

const daySection = (day) => `
    <section class="day">
      <h2>${escapeHtml(day.label)} <span class="count">(${activityCountLabel(day.activities.length)})</span></h2>
      <table>
        <thead>
          <tr>
            <th class="time">Vreme</th>
            <th>Kategorija</th>
            <th>Naziv aktivnosti</th>
            <th>Doziranje</th>
            <th>Napomena</th>
          </tr>
        </thead>
        <tbody>${day.activities.map(activityRow).join('')}
        </tbody>
      </table>
    </section>`;

const metaRow = (label, value) => `
        <tr><th>${escapeHtml(label)}</th><td>${escapeHtml(value)}</td></tr>`;

/**
 * Renders the whole protocol as a standalone HTML document, kept in sync with
 * what the review step shows on screen. The browser's print dialog turns it
 * into the PDF, and takes its default filename from the <title>.
 *
 * `protocol` is the wizard's form shape (`{ name, days, ... }`), which the
 * saved-protocol detail response also matches.
 */
export const buildProtocolExportHtml = (
  protocol,
  { subcategoriesLabel, categoriesLabel } = {}
) => {
  const title = protocol.name?.sr || 'Protokol';
  const daysWithActivities = WEEKDAYS.map(({ key, label }) => ({
    label,
    activities:
      protocol.days?.find((day) => day.dayOfWeek === key)?.activities || [],
  })).filter((day) => day.activities.length > 0);

  const totalActivities = daysWithActivities.reduce(
    (sum, day) => sum + day.activities.length,
    0
  );

  const meta = [
    metaRow('Kategorije', categoriesLabel || '-'),
    metaRow('Podkategorije', subcategoriesLabel || '-'),
    metaRow('Trajanje', labelFor(DURATION_OPTIONS, protocol.durationDays)),
    metaRow('Opseg težine', labelsFor(protocol.weightRanges, WEIGHT_RANGE_OPTIONS)),
    protocol.versionNumber
      ? metaRow('Verzija', `v${protocol.versionNumber}`)
      : '',
    metaRow('Broj dana', `${daysWithActivities.length}`),
    metaRow('Ukupno aktivnosti', `${totalActivities}`),
    protocol.versionNotes
      ? metaRow('Napomena o verziji', protocol.versionNotes)
      : '',
  ].join('');

  const body =
    daysWithActivities.length === 0
      ? '<p class="empty">Nema definisanih aktivnosti.</p>'
      : daysWithActivities.map(daySection).join('');

  return `<!DOCTYPE html>
<html lang="sr">
<head>
<meta charset="utf-8" />
<title>${escapeHtml(title)}</title>
<style>
  @page { size: A4; margin: 15mm; }
  body {
    font-family: Arial, "Helvetica Neue", Helvetica, sans-serif;
    color: #222;
    font-size: 12px;
    line-height: 1.45;
    margin: 0;
  }
  h1 { font-size: 20px; margin: 0 0 4px; }
  h2 {
    font-size: 14px;
    margin: 0 0 6px;
    padding-bottom: 4px;
    border-bottom: 2px solid #1e7e34;
  }
  .count { font-weight: normal; color: #666; font-size: 12px; }
  header { margin-bottom: 16px; }
  .subtitle { color: #666; margin: 0 0 12px; }
  .cover { width: 120px; height: 120px; object-fit: cover; border-radius: 6px; margin-bottom: 12px; }
  table { width: 100%; border-collapse: collapse; }
  .meta { width: auto; margin-bottom: 8px; }
  .meta th, .meta td { border: none; padding: 2px 12px 2px 0; text-align: left; vertical-align: top; }
  .meta th { width: 140px; color: #666; font-weight: normal; }
  .day { margin-bottom: 18px; page-break-inside: avoid; }
  .day table th, .day table td {
    border: 1px solid #ddd;
    padding: 5px 7px;
    text-align: left;
    vertical-align: top;
  }
  .day table th { background: #f4f6f5; font-weight: bold; }
  .day table td.time, .day table th.time { width: 52px; white-space: nowrap; }
  .name span { vertical-align: middle; }
  .thumb {
    width: 18px; height: 18px; object-fit: cover;
    border-radius: 3px; margin-right: 6px; vertical-align: middle;
  }
  .empty { color: #888; }
  footer { margin-top: 20px; color: #888; font-size: 11px; }
</style>
</head>
<body>
  <header>
    ${protocol.imageUrl ? `<img class="cover" src="${escapeHtml(protocol.imageUrl)}" alt="" />` : ''}
    <h1>${escapeHtml(title)}</h1>
    <p class="subtitle">Protokol — nedeljni raspored aktivnosti</p>
    <table class="meta"><tbody>${meta}
    </tbody></table>
  </header>
  ${body}
  <footer>Dokument generisan ${dayjs().format('DD.MM.YYYY. HH:mm')}</footer>
</body>
</html>`;
};

// Base64 images decode asynchronously even though nothing goes over the wire,
// so printing before they settle would drop them from the PDF.
const waitForImages = (doc) => {
  const images = Array.from(doc.images || []);
  if (images.length === 0) return Promise.resolve();

  return Promise.all(
    images.map((image) =>
      image.complete
        ? Promise.resolve()
        : new Promise((resolve) => {
            image.addEventListener('load', resolve, { once: true });
            image.addEventListener('error', resolve, { once: true });
          })
    )
  );
};

/**
 * Opens the browser's print dialog on an off-screen render of the protocol,
 * where the user picks "Save as PDF" (or prints straight to paper). Going
 * through the browser's own renderer is what keeps č/ć/š/ž/đ and the layout
 * intact — canvas- and jsPDF-based exports both struggle with those.
 */
export const exportProtocolToPdf = async (protocol, options) => {
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.cssText =
    'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
  document.body.appendChild(iframe);

  const cleanUp = () => {
    if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
  };

  try {
    const doc = iframe.contentDocument;
    doc.open();
    doc.write(buildProtocolExportHtml(protocol, options));
    doc.close();

    await waitForImages(doc);

    // Chrome returns from print() only once the dialog closes; Safari and
    // Firefox return immediately, so the timeout is the fallback that keeps
    // the iframe from leaking.
    iframe.contentWindow.addEventListener('afterprint', cleanUp, {
      once: true,
    });
    iframe.contentWindow.focus();
    iframe.contentWindow.print();
    setTimeout(cleanUp, 60000);
  } catch (error) {
    cleanUp();
    throw error;
  }
};
